/**
 * The look of the two instructor-facing landing pages in this repo:
 * /instructors/join (the invitation) and /instructors/signup (the form).
 *
 * WHERE IT CAME FROM, AND WHERE IT DELIBERATELY DIVERGES
 * The palette is anchored to web-frontend's student invite (accept-invite.jsx)
 * (cream ground, `#c01e2e` brand, white cards, pill buttons) because an
 * instructor and their students are joining the same product and the two
 * invitations should not read as two companies. An earlier version of both
 * pages was dark (a glass card lifted from Login.jsx); that was the problem
 * this replaced.
 *
 * What is NOT borrowed is that page's shape. accept-invite is a 480px column
 * of form fields and plain white rows, because it is filled in on a phone from
 * an email link and its whole job is data entry. These two are marketing
 * pages, read on a laptop off a link from the website, and rendered that way
 * they were a narrow grey ribbon down the middle of an empty screen. So: full
 * width, colour, soft shadows, round corners, one screen.
 *
 * THE ACCENT COLOURS ARE LOAD-BEARING, NOT DECORATION. Each step of the
 * journey owns one, and it is the same colour on its number, its icon bubble
 * and its border, which is what lets four steps read as four steps at a
 * glance rather than as a wall of equally-weighted cards.
 */

export const cream = "#FAF7F2";
export const ink = "#1a1a1a";
export const body = "#71717a";
export const brand = "#c01e2e";
export const hairline = "rgba(0,0,0,0.08)";
export const hairlineStrong = "rgba(0,0,0,0.15)";

// One per step, in the order they happen. `soft` is the icon bubble, `tint`
// the card wash. Both are kept very pale so four of them side by side still read
// as one component rather than as four competing posters.
export const STEP_COLORS = [
  { key: "amber",  strong: "#d97706", soft: "#fef0d5", tint: "rgba(217,119,6,0.05)" },
  { key: "teal",   strong: "#0891b2", soft: "#d9f2f8", tint: "rgba(8,145,178,0.05)" },
  { key: "violet", strong: "#7c3aed", soft: "#e9e2fb", tint: "rgba(124,58,237,0.05)" },
  { key: "green",  strong: "#059669", soft: "#d6f3e7", tint: "rgba(5,150,105,0.05)" },
];

export const iv = {
  page: {
    minHeight: "100vh",
    background: cream,
    position: "relative",
    overflowX: "hidden",
    padding: "0 20px 32px",
  },
  container: {
    width: "100%",
    maxWidth: "1120px",
    margin: "0 auto",
    position: "relative",
  },
  // A form is still a form: a column of fields stretched to 1120px is
  // unreadable no matter how much room the page has.
  card: {
    background: "#fff",
    border: `1px solid ${hairline}`,
    borderRadius: "24px",
    padding: { xs: "22px", sm: "26px" },
    boxShadow: "0 18px 50px -24px rgba(26,26,26,0.22)",
  },
  heading: {
    fontWeight: 800,
    color: ink,
    letterSpacing: "-1.4px",
    lineHeight: 1.06,
    margin: 0,
  },
  lead: {
    color: body,
    lineHeight: 1.6,
    margin: 0,
  },
  fieldWrap: {
    marginBottom: "14px",
  },
  label: {
    display: "block",
    fontSize: "13.5px",
    fontWeight: 600,
    color: ink,
    marginBottom: "7px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    background: "#fff",
    border: `1px solid ${hairlineStrong}`,
    borderRadius: "12px",
    color: ink,
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease",
    fontFamily: "inherit",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: body,
    display: "flex",
    alignItems: "center",
  },
  hint: {
    fontSize: "12.5px",
    color: body,
    marginTop: "6px",
    lineHeight: 1.5,
  },
  btnPrimary: {
    padding: "15px 32px",
    background: `linear-gradient(135deg, ${brand} 0%, #e04357 100%)`,
    border: "none",
    borderRadius: "999px",
    color: "#fff",
    fontSize: "16px",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    textDecoration: "none",
    fontFamily: "inherit",
    boxShadow: "0 10px 24px -8px rgba(192,30,46,0.6)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 30px -10px rgba(192,30,46,0.65)",
    },
  },
  btnSecondary: {
    padding: "15px 28px",
    background: "#fff",
    border: `1px solid ${hairlineStrong}`,
    borderRadius: "999px",
    color: "#3f3f46",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    textDecoration: "none",
    fontFamily: "inherit",
    transition: "transform 0.15s ease, border-color 0.15s ease",
    "&:hover": { transform: "translateY(-2px)", borderColor: ink },
  },
  footLink: {
    color: ink,
    fontWeight: 600,
  },
};

// Subject picker on the signup form.
export const chip = (selected) => ({
  padding: "7px 13px",
  borderRadius: "999px",
  border: `1px solid ${selected ? brand : hairlineStrong}`,
  background: selected ? brand : "#fff",
  color: selected ? "#fff" : "#52525b",
  fontSize: "12.5px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.15s ease",
  fontFamily: "inherit",
});

/**
 * The soft colour behind everything. Blobs rather than a flat wash because a
 * flat cream page with four white cards on it was the version that got called
 * basic. This is what makes it read as a landing page and not as a form.
 * Pointer-events off and fixed sizes: it must never intercept a click or push
 * the layout around on a narrow screen.
 */
export const BLOBS = [
  { color: "rgba(192,30,46,0.16)",  size: 520, top: "-14%", left: "-10%",  delay: "0s" },
  { color: "rgba(124,58,237,0.14)", size: 460, top: "34%",  right: "-12%", delay: "-4s" },
  { color: "rgba(8,145,178,0.13)",  size: 380, bottom: "-12%", left: "18%", delay: "-8s" },
  { color: "rgba(217,119,6,0.13)",  size: 300, top: "6%",   right: "22%",  delay: "-2s" },
];

export const blobKeyframes = `
  @keyframes ivFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%      { transform: translate(26px, -22px) scale(1.06); }
  }
  @keyframes ivRise {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
